// Motor de bitácora UCI — filas NOMBRABLES de una tabla horaria (Ola 2, M2).
//
// El papel de 113/114/115 deja, en medicación, infusiones, nebulización, drenajes, ingresos y varios "otros", un bloque de 5 a 20
// renglones en blanco donde la enfermera escribe el NOMBRE (fármaco, solución, tipo de drenaje) y, en las 24 columnas, la cantidad de cada
// hora. Antes esos bloques eran UNA sola fila fija ("Medicación") y no se sabía qué fármaco era cada valor. Aquí cada renglón es
// `{ nombre, horas }` y el grupo es una lista con tope (`TOPE_FILAS_NOMBRABLES`): se parte de unas pocas filas visibles y se agregan
// más con un botón, como en el papel se usa otro renglón.
//
// Solo lógica pura (sin React): la pantalla está en `FilasNombrablesHorarias.tsx`.
// Datos viejos: los estados guardados antes de esta ola no se rehidratan (cada formulario arranca en blanco y lo firmado ya es texto en
// un `Par[]`), así que no hay nada que migrar; el `Par` viejo "Medicación" sigue leyéndose tal cual en el documento firmado.
import { numeroATexto, sumaFilasHorarias } from '../calculosClinicosDatos';
import { COLUMNAS_HORARIAS_UCI, horaValorTexto, type ColumnaHorariaUci, type EstadoTablaHoraria24h } from './TablaHoraria24h';

/** Tope de renglones por grupo: el papel más grande (infusiones de 113) trae ~16-20 renglones. */
export const TOPE_FILAS_NOMBRABLES = 20;

export interface FilaNombrable {
  readonly nombre: string;
  readonly horas: Readonly<Record<ColumnaHorariaUci, string>>;
}

export type EstadoFilasNombrables = readonly FilaNombrable[];

function horasVacias(): Record<ColumnaHorariaUci, string> {
  return Object.fromEntries(COLUMNAS_HORARIAS_UCI.map((c) => [c, ''])) as Record<ColumnaHorariaUci, string>;
}

/** Estado inicial: `visibles` renglones en blanco. */
export function estadoInicialFilasNombrables(visibles: number): EstadoFilasNombrables {
  return Array.from({ length: Math.min(Math.max(visibles, 0), TOPE_FILAS_NOMBRABLES) }, () => ({ nombre: '', horas: horasVacias() }));
}

/** Agrega un renglón en blanco (no pasa del `tope`). */
export function agregarFilaNombrable(estado: EstadoFilasNombrables, tope: number = TOPE_FILAS_NOMBRABLES): EstadoFilasNombrables {
  return estado.length >= tope ? estado : [...estado, { nombre: '', horas: horasVacias() }];
}

/** Quita el renglón `indice`; si es el único, lo deja en blanco (el grupo nunca queda sin renglones). */
export function quitarFilaNombrable(estado: EstadoFilasNombrables, indice: number): EstadoFilasNombrables {
  if (estado.length <= 1) return estado.map((f, i) => (i === indice ? { nombre: '', horas: horasVacias() } : f));
  return estado.filter((_, i) => i !== indice);
}

export function renombrarFilaNombrable(estado: EstadoFilasNombrables, indice: number, nombre: string): EstadoFilasNombrables {
  return estado.map((f, i) => (i === indice ? { ...f, nombre } : f));
}

export function actualizarCeldaFilaNombrable(estado: EstadoFilasNombrables, indice: number, columna: ColumnaHorariaUci, valor: string): EstadoFilasNombrables {
  return estado.map((f, i) => (i === indice ? { ...f, horas: { ...f.horas, [columna]: valor } } : f));
}

function resumenHoras(f: FilaNombrable): string {
  return COLUMNAS_HORARIAS_UCI.filter((c) => (f.horas[c] ?? '').trim() !== '')
    .map((c) => horaValorTexto(c, f.horas[c]))
    .join(' · ');
}

/** ¿El renglón tiene algo escrito (nombre o alguna hora)? */
export function filaNombrableConDato(f: FilaNombrable): boolean {
  return f.nombre.trim() !== '' || resumenHoras(f) !== '';
}

/**
 * `Par[]` del grupo: un par por renglón con dato, "<grupo> — <nombre>" (o "<grupo> <n°>" si no se nombró) = "10 h: valor · …". Un renglón con
 * nombre y sin cantidades sale igual, con el texto `sinRegistroHorario` que pasa el llamador (i18n; el fármaco indicado sin dosis anotadas es un dato clínico, no un vacío).
 */
export function paresDeFilasNombrables(grupo: string, estado: EstadoFilasNombrables, sinRegistroHorario: string): { pregunta: string; respuesta: string }[] {
  return estado.flatMap((f, i) => {
    if (!filaNombrableConDato(f)) return [];
    const nombre = f.nombre.trim();
    return [{ pregunta: nombre !== '' ? `${grupo} — ${nombre}` : `${grupo} ${i + 1}`, respuesta: resumenHoras(f) || sinRegistroHorario }];
  });
}

/**
 * Suma del grupo, hora por hora, como UNA fila horaria: sirve para alimentar el balance hídrico (que suma filas de una
 * `EstadoTablaHoraria24h`) con el mismo criterio de lectura de números (miles a la española, celdas no numéricas ignoradas).
 */
export function filaSumaDeFilasNombrables(estado: EstadoFilasNombrables): Record<ColumnaHorariaUci, string> {
  const suma = horasVacias();
  for (const c of COLUMNAS_HORARIAS_UCI) {
    const celdas = Object.fromEntries(estado.map((f, i) => [String(i), { [c]: f.horas[c] ?? '' }]));
    const total = sumaFilasHorarias(celdas, Object.keys(celdas));
    suma[c] = total === undefined ? '' : numeroATexto(total);
  }
  return suma;
}

/** `base` + una fila sintética por grupo (clave = la que usa la configuración del balance) con la suma horaria del grupo. */
export function tablaConGruposNombrables(base: EstadoTablaHoraria24h, grupos: Readonly<Record<string, EstadoFilasNombrables>>): EstadoTablaHoraria24h {
  return { ...base, ...Object.fromEntries(Object.entries(grupos).map(([clave, estado]) => [clave, filaSumaDeFilasNombrables(estado)])) };
}
